import { useUiStore, useAuthStore, useSettingsStore } from '@/stores';
import { LogOut, Download, Wifi, WifiOff, RefreshCw, MapPin } from 'lucide-react';
import { clsx } from 'clsx';
import { useToast } from '@/components/common/Toast';
import { exportStandaloneHtml } from '@/utils/exportHtml';
import { useNetworkStatus } from '@/hooks/useNetworkStatus';
import { resolveLocationName } from '@/utils/locationResolution';

export function Header() {
  const currentView = useUiStore((s) => s.currentView);
  const { user, locations, locationId, setLocationId, logout, provider } = useAuthStore();
  const { locationAliases, locationNameMode } = useSettingsStore();
  const { showToast } = useToast();

  const handleExport = () => {
    try {
      exportStandaloneHtml();
      showToast('Copia de seguridad descargada con éxito', 'success');
    } catch (err) {
      console.error('Error al exportar snapshot:', err);
      showToast('No se pudo descargar la copia de seguridad', 'error');
    }
  };

  const titles: Record<string, string> = {
    appointments: 'Citas & Agenda',
    campaigns: 'Campañas',
    crm: 'CRM',
    dashboard: 'Dashboard',
    settings: 'Ajustes',
  };

  return (
    <header className="sticky top-0 z-30 bg-surface/80 backdrop-blur-md border-b border-border px-4 py-2.5 flex items-center justify-between gap-3">
      <div className="flex items-center gap-3 min-w-0">
        <div>
          <h1 className="text-base font-bold text-text-primary leading-tight truncate">
            {titles[currentView] || 'Fresha Hub'}
          </h1>
          {provider?.data?.attributes?.name && (
            <p className="text-[10px] text-text-muted truncate">
              {provider.data.attributes.name}
            </p>
          )}
        </div>

        {/* Location Dropdown */}
        {locations.length > 0 && (
          <div className="flex items-center gap-1 bg-surface-secondary border border-border rounded-lg px-2 py-1 text-xs">
            <MapPin size={13} className="text-brand-primary flex-shrink-0" />
            <select
              value={locationId || ''}
              onChange={(e) => setLocationId(e.target.value)}
              className="bg-transparent text-text-primary font-medium focus:outline-none cursor-pointer text-xs max-w-[140px] truncate"
            >
              <option value="all" className="bg-surface text-text-primary font-semibold">
                Todas las sedes
              </option>
              {locations.map((loc) => (
                <option key={loc.id} value={loc.id} className="bg-surface text-text-primary">
                  {resolveLocationName(loc.id, loc.attributes?.name, locations, locationAliases, locationNameMode)}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      <div className="flex items-center gap-2 flex-shrink-0">
        {user && (
          <div className="hidden sm:flex items-center gap-1.5 px-2 py-1 bg-surface-secondary border border-border rounded-lg text-xs">
            <span className="font-medium text-text-primary truncate max-w-[110px]">
              {user.name || user.username}
            </span>
            <span
              className={clsx(
                'text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded',
                user.role === 'admin'
                  ? 'bg-purple-500/10 text-purple-400 border border-purple-500/20'
                  : 'bg-blue-500/10 text-blue-400 border border-blue-500/20'
              )}
            >
              {user.role}
            </span>
          </div>
        )}

        <button
          onClick={handleExport}
          className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium bg-surface-secondary hover:bg-surface-secondary/80 text-text-primary border border-border transition-colors cursor-pointer"
          title="Descargar copia de respaldo para consulta sin conexión"
        >
          <Download size={14} />
          <span className="hidden md:inline">Respaldo</span>
        </button>

        <SyncAndOnlineIndicator />

        <button
          onClick={logout}
          className="p-1.5 rounded-lg hover:bg-surface-secondary text-text-secondary transition-colors cursor-pointer"
          title="Cerrar sesión"
        >
          <LogOut size={16} />
        </button>
      </div>
    </header>
  );
}

function SyncAndOnlineIndicator() {
  const { isOnline, isReconnecting, lastOnlineAt } = useNetworkStatus();
  const isSyncing = useSettingsStore((s) => s.isSyncing);
  const lastSyncedAt = useSettingsStore((s) => s.lastSyncedAt);

  return (
    <div className="flex items-center gap-1.5 px-2 py-1 bg-surface-secondary/70 border border-border rounded-lg text-xs">
      {/* Network Status */}
      {isReconnecting ? (
        <div
          title="Reconectando con el servidor..."
          className="flex items-center gap-1 text-blue-500"
        >
          <RefreshCw size={13} className="animate-spin text-blue-500 flex-shrink-0" />
          <span className="text-[10px] font-medium hidden sm:inline text-blue-500">
            Reconectando...
          </span>
        </div>
      ) : isOnline ? (
        <div
          title="En línea y conectado a Fresha Hub"
          className="flex items-center gap-1 text-brand-success"
        >
          <Wifi size={13} className="text-brand-success flex-shrink-0" />
          <span className="text-[10px] font-medium hidden sm:inline text-brand-success">
            En línea
          </span>
        </div>
      ) : (
        <div
          title={
            lastOnlineAt
              ? `Sin conexión - Funcionando con datos locales (Última vez en línea: ${lastOnlineAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })})`
              : 'Sin conexión - Funcionando con datos locales'
          }
          className="flex items-center gap-1 text-brand-warning"
        >
          <WifiOff size={13} className="text-brand-warning flex-shrink-0" />
          <span className="text-[10px] font-medium hidden sm:inline text-brand-warning">
            Sin conexión (Modo local)
          </span>
        </div>
      )}

      {/* Cloud Sync Status */}
      {isSyncing ? (
        <div title="Sincronizando ajustes con la nube..." className="flex items-center text-brand-primary">
          <span className="w-1.5 h-1.5 rounded-full bg-brand-primary animate-ping" />
        </div>
      ) : lastSyncedAt ? (
        <div
          title={`Ajustes en la nube sincronizados (${new Date(lastSyncedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })})`}
          className="flex items-center text-text-muted"
        >
          <span className="w-1.5 h-1.5 rounded-full bg-brand-success" />
        </div>
      ) : null}
    </div>
  );
}
